import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import {
  Print,
  AttachMoney,
  Devices,
  Group,
  Warning
} from '@mui/icons-material';
import { biAnalyticsService } from '../services/biAnalytics';
import { BiDashboard as BiDashboardType, TopClient, DeviceAlert } from '../types/bi';

export default function BIDashboard() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<BiDashboardType | null>(null);
  const [topClients, setTopClients] = useState<TopClient[]>([]);
  const [alerts, setAlerts] = useState<DeviceAlert[]>([]);
  const [days, setDays] = useState(30);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, [days]);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, clientsData, alertsData] = await Promise.all([
        biAnalyticsService.getDashboard(days),
        biAnalyticsService.getTopClients(10, days),
        biAnalyticsService.getDeviceAlerts()
      ]);
      setDashboard(dashboardData);
      setTopClients(clientsData);
      setAlerts(alertsData);
    } catch (error: any) {
      console.error('Error loading BI dashboard:', error);
      setError(error.response?.data?.detail || 'Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('es-PA').format(num);
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box p={3}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Business Intelligence Dashboard
          </Typography>
        <Box>
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              variant={days === d ? 'contained' : 'outlined'}
              onClick={() => setDays(d)}
              sx={{ mr: 1 }}
            >
              {d} Days
            </Button>
          ))}
        </Box>
      </Box>

      {/* Overview Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Devices color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Active Devices
                </Typography>
              </Box>
              <Typography variant="h4">{dashboard.overview.total_devices}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Group color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Active Clients
                </Typography>
              </Box>
              <Typography variant="h4">{dashboard.overview.total_clients}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Print color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Total Pages ({days}d)
                </Typography>
              </Box>
              <Typography variant="h4">
                {formatNumber(dashboard.printing_volume.total_pages)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                BW: {formatNumber(dashboard.printing_volume.bw_pages)} |
                Color: {formatNumber(dashboard.printing_volume.color_pages)}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AttachMoney color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  Revenue ({days}d)
                </Typography>
              </Box>
              <Typography variant="h4">
                {formatCurrency(dashboard.revenue.total_billed)}
              </Typography>
              <Typography variant="caption" color="warning.main">
                Outstanding: {formatCurrency(dashboard.revenue.outstanding)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Top Clients */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Clients by Volume
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Pages</TableCell>
                      <TableCell align="right">Devices</TableCell>
                      <TableCell align="right">Avg/Device</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topClients.map((client) => (
                      <TableRow key={client.client_id}>
                        <TableCell>{client.client_name}</TableCell>
                        <TableCell align="right">{formatNumber(client.total_pages)}</TableCell>
                        <TableCell align="right">{client.device_count}</TableCell>
                        <TableCell align="right">
                          {formatNumber(client.avg_pages_per_device)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Device Alerts */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Device Alerts
              </Typography>
              {alerts.length === 0 ? (
                <Typography color="textSecondary">No alerts - All devices operating normally</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Serial</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.map((alert) => (
                        <TableRow key={alert.equipment_id}>
                          <TableCell>{alert.serial_number}</TableCell>
                          <TableCell>{alert.client_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={alert.status}
                              color="warning"
                              size="small"
                              icon={<Warning />}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
    </>
  );

}
